<script setup>
import MessageIcon from '~/components/icons/MessageIcon.vue'
import CrossIcon from '~/components/icons/CrossIcon.vue'
import SendIcon from '~/components/icons/SendIcon.vue'
import SearchIcon from '~/components/icons/SearchIcon.vue'
import {formatChatHtml} from '~/utils/chatFormat.js'

const PRIMARY_TOPICS = ['track', 'samples', 'return']
const EXTRA_TOPICS = ['quote', 'products', 'delivery', 'showroom', 'damage', 'claims', 'stock', 'payment', 'human']

const {t} = useI18n()
const {isOpen, unreadCount, messages, sending, awaitingReply, close, toggle, loadHistory, sendMessage} = useChat()

const draft = ref('')
const threadRef = ref(null)
const inputRef = ref(null)
const startedChat = ref(false)
const showMoreFaqs = ref(false)

const hasConversation = computed(() =>
    messages.value.some((message) => message.id !== 'greeting' && message.role !== 'system')
)
const showHome = computed(() => !startedChat.value && !hasConversation.value)
const visibleTopics = computed(() => showMoreFaqs.value
    ? [...PRIMARY_TOPICS, ...EXTRA_TOPICS]
    : PRIMARY_TOPICS
)
const homeTime = computed(() => new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}))

function scrollToBottom(force = true) {
  nextTick(() => {
    const el = threadRef.value
    if (!el) {
      return
    }

    if (!force) {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      if (distanceFromBottom > 80) {
        return
      }
    }

    el.scrollTop = el.scrollHeight
  })
}

function startChat() {
  startedChat.value = true
  nextTick(() => inputRef.value?.focus())
}

async function send() {
  const value = draft.value.trim()
  if (!value || sending.value) {
    return
  }

  startedChat.value = true
  draft.value = ''
  try {
    await sendMessage(value)
    scrollToBottom(true)
  } catch {
    draft.value = value
  }

  nextTick(() => inputRef.value?.focus())
}

function onSubmit() {
  send()
}

function onKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    send()
  }
}

function onEscape(event) {
  if (event.key === 'Escape' && isOpen.value) {
    close()
  }
}

watch(isOpen, (openState) => {
  if (import.meta.client) {
    document.body.classList.toggle('hc-chat-open', openState)
  }

  if (openState) {
    unreadCount.value = 0
    scrollToBottom(true)
    if (!showHome.value) {
      nextTick(() => inputRef.value?.focus())
    }
  }
})

watch(
    () => [
      messages.value.length,
      messages.value[messages.value.length - 1]?.id,
      awaitingReply.value,
    ],
    () => {
      if (isOpen.value && !showHome.value) {
        scrollToBottom(false)
      }
    }
)

onMounted(() => {
  window.addEventListener('keydown', onEscape)
  loadHistory()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onEscape)
  document.body.classList.remove('hc-chat-open')
})
</script>

<template>
  <Teleport to="body">
    <div class="chat-widget" :class="{open: isOpen}">
      <transition name="chat-panel">
        <section
            v-if="isOpen"
            class="chat-panel"
            :class="{home: showHome}"
            role="dialog"
            aria-modal="true"
            :aria-label="t('chat.title')"
        >
          <header v-if="!showHome" class="chat-header">
            <div class="header-copy">
              <strong>{{ t('chat.title') }}</strong>
              <span>{{ t('chat.subtitle') }}</span>
            </div>
            <button class="icon-btn" type="button" :aria-label="t('chat.close')" @click="close">
              <CrossIcon/>
            </button>
          </header>

          <div v-if="!showHome" class="agent-bar">
            <span class="avatar" aria-hidden="true">HC</span>
            <span>{{ t('chat.agent') }}</span>
          </div>

          <div v-if="showHome" class="home">
            <section class="home-card home-hero">
              <h2 class="home-title">{{ t('chat.home_title') }}</h2>
              <button class="home-close" type="button" :aria-label="t('chat.close')" @click="close">
                <CrossIcon/>
              </button>
            </section>

            <button class="home-card home-card--chat" type="button" @click="startChat">
              <span class="home-card__icon" aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect x="7" y="4" width="14" height="18" rx="2" stroke="#3d4a63" stroke-width="1.6"/>
                  <path d="M10 9h8M10 13h8M10 17h5" stroke="#3d4a63" stroke-width="1.6" stroke-linecap="round"/>
                  <circle cx="20" cy="20" r="5" fill="#f3f4f7" stroke="#3d4a63" stroke-width="1.4"/>
                  <path d="M18.2 20.2c.5.7 1.3 1.1 2 1.1s1.5-.4 2-1.1" stroke="#3d4a63" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </span>
              <span class="home-card__copy">
                <strong>{{ t('chat.title') }}</strong>
                <span>{{ t('chat.greeting') }}</span>
              </span>
              <time>{{ homeTime }}</time>
            </button>

            <section class="home-card home-faq">
              <header class="home-faq__head">
                <h3>{{ t('chat.faq_title') }}</h3>
                <span class="home-faq__search" aria-hidden="true">
                  <SearchIcon/>
                </span>
              </header>
              <ul>
                <li v-for="topic in visibleTopics" :key="topic">
                  <button type="button" @click="startChat">
                    <span class="home-faq__icon" :data-topic="topic" aria-hidden="true"></span>
                    <span>{{ t(`chat.topics.${topic}`) }}</span>
                  </button>
                </li>
              </ul>
              <button class="home-faq__more" type="button" @click="showMoreFaqs = !showMoreFaqs">
                {{ showMoreFaqs ? t('chat.faq_less') : t('chat.faq_more') }}
              </button>
            </section>
          </div>

          <div v-else ref="threadRef" class="thread">
            <article
                v-for="message in messages"
                v-show="message.text || message.role === 'system'"
                :key="message.id"
                class="message"
                :class="message.role"
            >
              <template v-if="message.role === 'system'">
                <p v-html="formatChatHtml(message.text)"></p>
              </template>
              <template v-else>
                <span v-if="message.role === 'agent'" class="author">{{ message.author || t('chat.agent') }}</span>
                <p v-html="formatChatHtml(message.text)"></p>
                <time>{{ message.time }}</time>
              </template>
            </article>
            <article v-if="awaitingReply" class="message agent">
              <span class="author">{{ t('chat.agent') }}</span>
              <div class="typing" aria-hidden="true">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </article>
          </div>

          <form v-if="!showHome" class="composer" @submit.prevent="onSubmit">
            <textarea
                ref="inputRef"
                v-model="draft"
                rows="1"
                :placeholder="t('chat.placeholder')"
                :aria-label="t('chat.placeholder')"
                @keydown="onKeydown"
            />
            <button type="submit" class="send" :disabled="!draft.trim() || sending" :aria-label="t('chat.send')">
              <SendIcon/>
            </button>
          </form>
        </section>
      </transition>

      <button
          class="launcher"
          type="button"
          :aria-label="isOpen ? t('chat.close') : t('chat.open')"
          :aria-expanded="isOpen"
          @click="toggle"
      >
        <CrossIcon v-if="isOpen"/>
        <MessageIcon v-else width="26px" height="26px" stroke="#18181f"/>
        <span v-if="!isOpen && unreadCount" class="badge">{{ unreadCount }}</span>
      </button>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.chat-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 100000;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;

  @include mobile {
    right: 16px;
    bottom: 16px;
  }

  > * {
    pointer-events: auto;
  }
}

.chat-panel {
  width: 380px;
  height: min(580px, calc(100dvh - 172px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: $white;
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(24, 24, 31, 0.22);
  transform-origin: bottom right;

  &.home {
    height: auto;
    max-height: min(640px, calc(100dvh - 100px));
    background: #eef0f4;
  }

  @include mobile {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: $space-black;
  color: $white;
}

.header-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
  }

  span {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.72);
  }
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;

  :deep(path) {
    stroke: $white;
  }
}

.agent-bar {
  display: flex;
  flex: none;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid $border-grey;
  color: $title-grey;
  font-size: 13px;
}

.avatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: $yellow;
  color: $dark-black;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.thread {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  background: $white;
}

.home {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
}

.home-card {
  display: block;
  width: 100%;
  margin: 0;
  padding: 16px;
  background: $white;
  border: 0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(24, 24, 31, 0.06);
  text-align: left;
}

.home-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.home-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #2c3a51;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.home-close {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;

  :deep(path) {
    stroke: #8b93a3;
  }
}

.home-card--chat {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  time {
    align-self: flex-start;
    margin-left: auto;
    color: #9aa3b2;
    font-size: 12px;
    white-space: nowrap;
  }
}

.home-card__icon {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.home-card__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;

  strong {
    color: #2c3a51;
    font-size: 15px;
    font-weight: 700;
  }

  span {
    overflow: hidden;
    color: #8b93a3;
    font-size: 13px;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.home-faq {
  padding: 6px 4px 10px;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li + li {
    border-top: 1px solid #eceff3;
  }

  li button {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    background: transparent;
    border: 0;
    color: #2c3a51;
    font-size: 15px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
}

.home-faq__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid #eceff3;

  h3 {
    margin: 0;
    color: #2c3a51;
    font-size: 16px;
    font-weight: 700;
  }
}

.home-faq__search {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #dfe3ea;
  border-radius: 50%;
}

.home-faq__icon {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: #f3f5f8;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c6578' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='8'/%3E%3Cpath d='M12 8v8M8 12h8'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 18px;

  &[data-topic='track'] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c6578' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z'/%3E%3Ccircle cx='12' cy='10' r='2.2'/%3E%3C/svg%3E");
  }

  &[data-topic='samples'] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c6578' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 7h11v11H8z'/%3E%3Cpath d='M5 16V6h11'/%3E%3Cpath d='M11 11h5M11 14h3'/%3E%3C/svg%3E");
  }

  &[data-topic='return'] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c6578' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='7' width='14' height='12' rx='2'/%3E%3Cpath d='M9 7V5h6v2'/%3E%3Cpath d='M10 13H8.5A2.5 2.5 0 1 0 11 15.5'/%3E%3Cpath d='M10 13l1.6-1.6M10 13l1.6 1.6'/%3E%3C/svg%3E");
  }
}

.home-faq__more {
  width: 100%;
  padding: 12px 12px 4px;
  background: transparent;
  border: 0;
  color: #2c3a51;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.message {
  display: flex;
  flex-direction: column;
  max-width: 86%;
  margin-bottom: 12px;

    p {
    margin: 0;
    padding: 8px 12px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.45;
    word-break: break-word;

    :deep(strong) {
      font-weight: 700;
    }

    :deep(a) {
      color: inherit;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  time {
    margin-top: 4px;
    font-size: 11px;
    color: $disabled-text;
  }

  &.agent {
    align-items: flex-start;

    p {
      background: $grey;
      color: $dark-black;
      border-bottom-left-radius: 4px;
    }
  }

  &.user {
    margin-left: auto;
    align-items: flex-end;

    p {
      background: $space-black;
      color: $white;
      border-bottom-right-radius: 4px;
    }
  }

  &.system {
    max-width: 100%;
    align-items: center;
    margin: 8px 0 16px;

    p {
      padding: 0;
      background: transparent;
      color: $disabled-text;
      font-size: 12px;
      text-align: center;
    }
  }
}

.author {
  margin: 0 0 4px 4px;
  font-size: 12px;
  color: $title-grey;
}

.typing {
  display: flex;
  flex: none;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 10px 12px;
  background: $grey;
  border-radius: 16px 16px 16px 4px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $disabled-text;
  animation: chat-dot 1.2s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

.composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid $border-grey;
  background: $white;
}

textarea {
  flex: 1;
  max-height: 96px;
  min-height: 40px;
  resize: none;
  padding: 10px 12px;
  border: 1px solid $border-grey;
  border-radius: 10px;
  background: $light-grey;
  color: $dark-black;
  font-size: 14px;
  line-height: 1.4;

  &::placeholder {
    color: $disabled-text;
  }
}

.send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  background: $yellow;
  border: 0;
  border-radius: 10px;
  color: $dark-black;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.chat-widget.open {
  @include mobile {
    inset: 0;
    right: 0;
    bottom: 0;

    .launcher {
      display: none;
    }
  }
}

.launcher {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  background: $yellow;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(24, 24, 31, 0.18);
  cursor: pointer;

  :deep(path) {
    stroke: $space-black;
  }
}

.badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: $red;
  border-radius: 9px;
  color: $white;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
}

.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.chat-panel-enter-from,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@keyframes chat-dot {
  0%, 80%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
</style>

<style lang="scss">
body.hc-chat-open {
  @include mobile {
    overflow: hidden;
  }
}
</style>
